import secrets from "@/secrets";

export interface DeskProTicketRequest {
    subject: string;
    message: string;
    name: string;
    email: string;
}

export interface DeskProTicketResponse {
    data: {
        id: number;
        ref: string;
        subject: string;
        status: string;
    };
}

export interface DeskProError {
    message: string;
    errors?: {
        errors?: Array<{
            code: string;
            message: string;
        }>;
    };
}

export class DeskProApiError extends Error {
    constructor(message: string, public readonly statusCode?: number) {
        super(message);
        this.name = "DeskProApiError";
    }

    public static isInstance(err: Error): boolean {
        return err.name === "DeskProApiError";
    }
}

export class DeskProService {
    private baseUrl: string;
    private apiKey: string;
    private agentId: number;
    private departmentId: number | null;

    constructor() {
        if (!secrets.deskpro?.enabled) {
            throw new Error("DeskPro is not enabled in configuration");
        }

        this.baseUrl = secrets.deskpro.apiUrl;
        this.apiKey = secrets.deskpro.apiKey;
        this.agentId = secrets.deskpro.agentId;
        this.departmentId = secrets.deskpro.departmentId ?? null;
    }

    async createTicket(request: DeskProTicketRequest): Promise<DeskProTicketResponse> {
        const url = `${this.baseUrl}/tickets`;

        const payload: Record<string, unknown> = {
            subject: request.subject,
            message: {
                message: request.message,
                format: "html",
            },
            person: request.email,
        };

        if (this.departmentId != null) {
            payload.department = this.departmentId;
        }

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `key ${this.apiKey}`,
                    "X-DeskPRO-Agent-ID": this.agentId.toString(),
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const error: DeskProError = await response.json();
                const specificError = error.errors?.errors?.[0]?.message;
                const errorMessage = specificError || error.message || "Failed to create support ticket";
                throw new DeskProApiError(errorMessage, response.status);
            }

            return await response.json();
        } catch (error) {
            if (DeskProApiError.isInstance(error as Error)) {
                throw error;
            }
            console.error("DeskPro API Error:", error);
            throw new DeskProApiError("Failed to create support ticket");
        }
    }
}

export const deskProService = new DeskProService();
